import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "src/utils/axios";
import { getDeviceId } from "src/utils/deviceId";

// DeviceId = SessionIdentifier del sistema (tabla `Devices`). Reutilizamos
// el mismo ID en los 3 reporters de monitorización, así el backend puede
// hacer JOIN con `Devices` y mostrar el nombre legible ("TPV cocina",
// "Caja barra") en el panel.

// ─── Cola pendiente de envíos ────────────────────────────────────────────────
// Si la caída ocurre AHORA mismo no podemos enviar al backend (no hay red).
// Persistimos el evento en localStorage y al recuperar conexión se vacía la
// cola en orden de llegada. Cada entrada: { eventType, startedAt, endedAt?,
// latencyMsAvg, effectiveType, userAgent }.
const QUEUE_KEY = "tmo-connectivity-queue";
function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function writeQueue(items) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  } catch { /* quota / private mode */ }
}
function enqueue(item) {
  const q = readQueue();
  q.push(item);
  writeQueue(q);
}

/**
 * Reporter de conectividad: escucha el slice `networkStatus` y POSTea al
 * endpoint `/connectivity/event` cuando hay una caída o se recupera. El
 * objetivo es alimentar la tabla histórica que muestra Ajustes →
 * Monitorización (heatmap día×hora, lista cronológica, etc).
 *
 * Estrategia:
 *  - Apertura (offline detectado): se intenta enviar inmediatamente. Si
 *    falla (típico: ¡no hay red!) lo encolamos en localStorage.
 *  - Cierre (online detectado tras un offline): se envía con `endedAt`. El
 *    backend busca el "abierto" del mismo deviceId y lo cierra; si no
 *    existe (porque la apertura nunca llegó), inserta el evento ya cerrado
 *    con los dos timestamps.
 *  - Tras recuperar conexión vaciamos la cola.
 *
 * Source: el caller pasa qué app es ('tpv-camarero', 'kds', 'caja',
 * 'manage-admin'). Por defecto 'manage-admin' si se monta en el
 * DashboardLayout.
 */
export default function useConnectivityReporter({ source = "manage-admin" } = {}) {
  const isOnline = useSelector((s) => s.networkStatus?.isOnline);
  const avgLatencyMs = useSelector((s) => s.networkStatus?.avgLatencyMs);
  const effectiveType = useSelector((s) => s.networkStatus?.effectiveType);
  // El SWITCH GLOBAL de monitorización (slice `errors.isMonitoring`)
  // controla si este hook persiste eventos al backend. OFF por defecto →
  // ningún POST. Cuando el admin lo activa desde Ajustes →
  // Monitorización, los 3 reporters (caídas, latencia, peticiones API)
  // empiezan a grabar.
  const isMonitoring = useSelector((s) => s.errors?.isMonitoring);

  const prevOnline = useRef(isOnline);
  const offlineStartedAt = useRef(null);

  const flushQueue = async () => {
    const q = readQueue();
    if (q.length === 0) return;
    const remaining = [];
    for (const item of q) {
      try {
        await axios.post("/connectivity/event", item);
      } catch {
        remaining.push(item);
      }
    }
    writeQueue(remaining);
  };

  useEffect(() => {
    // Al activar el monitor (o al montar si ya estaba activo) vaciamos la
    // cola pendiente de sesiones anteriores.
    if (isOnline && isMonitoring) flushQueue();
  }, [isMonitoring]); // eslint-disable-line

  useEffect(() => {
    const prev = prevOnline.current;
    prevOnline.current = isOnline;

    if (!isMonitoring) return; // sin grabación: no POST, no cola.

    const baseFields = {
      source,
      deviceId: getDeviceId(),
      deviceLabel: typeof navigator !== "undefined" ? navigator.userAgent.split(")")[0].slice(0, 100) : null,
      latencyMsAvg: avgLatencyMs ? Math.round(avgLatencyMs) : null,
      effectiveType: effectiveType || null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 280) : null,
    };

    // Caída: online → offline
    if (prev === true && isOnline === false) {
      offlineStartedAt.current = new Date().toISOString();
      const payload = {
        ...baseFields,
        eventType: "offline",
        startedAt: offlineStartedAt.current,
      };
      axios
        .post("/connectivity/event", payload)
        .catch(() => enqueue(payload));
    }

    // Recuperación: offline → online
    if (prev === false && isOnline === true) {
      const startedAt = offlineStartedAt.current || new Date().toISOString();
      offlineStartedAt.current = null;
      const payload = {
        ...baseFields,
        eventType: "offline",
        startedAt,
        endedAt: new Date().toISOString(),
      };
      axios
        .post("/connectivity/event", payload)
        .catch(() => enqueue(payload));
      flushQueue();
    }
  }, [isOnline, avgLatencyMs, effectiveType, source, isMonitoring]);
}
